import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Plus,
  Edit3,
  Eye,
  Trash2,
  Power,
  PowerOff,
  Loader2,
  Filter,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  CATEGORIES,
  CONTENT_TYPES,
  formatDateTime,
} from "@/lib/cmsConstants";
import CMSFormDrawer from "@/components/cms/CMSFormDrawer";
import CMSPreviewModal from "@/components/cms/CMSPreviewModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TYPE_TESTID = {
  case_law: "case-laws",
  act: "acts",
  circular: "circulars",
  alert: "alerts",
  blog: "blogs",
};

export default function ContentManagerPage() {
  const [activeType, setActiveType] = useState("case_law");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [drawer, setDrawer] = useState(null); // { open, initial? }
  const [preview, setPreview] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: ["cms-list", activeType, q, statusFilter, categoryFilter],
    queryFn: () =>
      api.cmsList({
        contentType: activeType,
        q: q || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
        category: categoryFilter || undefined,
      }),
    staleTime: 30_000,
  });

  const summaryQ = useQuery({
    queryKey: ["cms-summary"],
    queryFn: api.cmsSummary,
    staleTime: 30_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["cms-list"] });
    qc.invalidateQueries({ queryKey: ["cms-summary"] });
    qc.invalidateQueries({ queryKey: ["public-cms"] });
    qc.invalidateQueries({ queryKey: ["public-landing-cms"] });
  };

  const createM = useMutation({
    mutationFn: (payload) => api.cmsCreate(payload),
    onSuccess: () => {
      toast.success("Content created");
      setDrawer(null);
      invalidate();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Create failed"),
  });

  const updateM = useMutation({
    mutationFn: ({ id, payload }) => api.cmsUpdate(id, payload),
    onSuccess: () => {
      toast.success("Content updated");
      setDrawer(null);
      invalidate();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  const toggleM = useMutation({
    mutationFn: (id) => api.cmsTogglePublish(id),
    onSuccess: (row) => {
      toast.success(
        row.status === "published" ? "Published to public site" : "Unpublished"
      );
      invalidate();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Toggle failed"),
  });

  const deleteM = useMutation({
    mutationFn: (id) => api.cmsDelete(id),
    onSuccess: () => {
      toast.success("Deleted");
      setConfirmDel(null);
      invalidate();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const rows = listQ.data?.items || [];
  const saving = createM.isPending || updateM.isPending;

  return (
    <div className="p-4 md:p-6 max-w-[1400px]" data-testid="cms-page">
      <header className="mb-5">
        <div className="text-[11px] uppercase tracking-wider text-[#64748B]">
          Admin
        </div>
        <h1 className="text-lg font-semibold text-[#0F172A]">Content Manager</h1>
        <p className="text-xs text-[#64748B] mt-0.5">
          Publish case laws, acts, circulars, alerts and blogs to the public
          site. Drafts stay invisible to non-admin users.
        </p>
      </header>

      {/* Summary stat cards */}
      <SummaryStats counts={summaryQ.data?.counts} onPick={setActiveType} activeType={activeType} />

      {/* Recent activity */}
      <RecentActivity items={summaryQ.data?.recent || []} />

      {/* Pill tabs */}
      <div
        className="flex flex-wrap items-center gap-1.5 mb-4"
        data-testid="cms-tabs"
      >
        {CONTENT_TYPES.map((t) => {
          const on = activeType === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveType(t.key)}
              data-testid={`cms-tab-${TYPE_TESTID[t.key]}`}
              className={`h-9 px-3.5 rounded-full text-[13px] font-medium border transition-colors ${
                on
                  ? "bg-[#0F172A] text-white border-[#0F172A]"
                  : "bg-white text-[#334155] border-[#E2E8F0] hover:bg-[#F8FAFC]"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="cms-search"
            placeholder="Search by title"
            className="w-full h-9 pl-9 pr-3 rounded-md border border-[#E2E8F0] bg-white text-sm outline-none focus:border-[#0F172A]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="cms-status-filter"
          className="h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm"
        >
          <option value="all">All statuses</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          data-testid="cms-category-filter"
          className="h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm"
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setDrawer({ open: true, initial: null })}
          data-testid="cms-new-btn"
          className="ml-auto h-9 px-3 rounded-md bg-[#0F172A] text-white text-sm font-medium inline-flex items-center gap-1.5 hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> New
        </button>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-[#E2E8F0] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] text-[#475569]">
            <tr>
              <Th>Title</Th>
              <Th>Category</Th>
              <Th>Status</Th>
              <Th>Updated</Th>
              <Th className="text-right pr-4">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {listQ.isLoading ? (
              <tr>
                <td colSpan={5} className="text-center py-10 text-[#64748B]">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-10 text-[#64748B]">
                  No content yet — click{" "}
                  <span className="font-medium text-[#0F172A]">New</span> to add
                  the first item.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]"
                  data-testid={`cms-row-${r.id}`}
                >
                  <td className="px-4 py-2.5 max-w-md">
                    <div className="text-[13px] font-medium text-[#0F172A] truncate">
                      {r.title}
                    </div>
                    {r.fields?.refNumber && (
                      <div className="text-[11px] text-[#64748B] truncate">
                        {r.fields.refNumber}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(r.category || []).map((c) => (
                        <span
                          key={c}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#334155]"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusPill status={r.status} />
                  </td>
                  <td className="px-4 py-2.5 text-[12px] text-[#64748B]">
                    {formatDateTime(r.updatedAt)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <IconBtn
                        title="Edit"
                        icon={Edit3}
                        onClick={() =>
                          setDrawer({ open: true, initial: r })
                        }
                        testId={`cms-edit-${r.id}`}
                      />
                      <IconBtn
                        title="Preview"
                        icon={Eye}
                        onClick={() => setPreview(r)}
                        testId={`cms-preview-${r.id}`}
                      />
                      <IconBtn
                        title={
                          r.status === "published" ? "Unpublish" : "Publish"
                        }
                        icon={r.status === "published" ? PowerOff : Power}
                        onClick={() => toggleM.mutate(r.id)}
                        testId={`cms-toggle-${r.id}`}
                      />
                      <IconBtn
                        title="Delete"
                        icon={Trash2}
                        danger
                        onClick={() => setConfirmDel(r)}
                        testId={`cms-delete-${r.id}`}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Drawer */}
      {drawer?.open && (
        <CMSFormDrawer
          open
          contentType={activeType}
          initial={drawer.initial}
          saving={saving}
          onCancel={() => setDrawer(null)}
          onSave={(payload) => {
            if (drawer.initial?.id) {
              updateM.mutate({ id: drawer.initial.id, payload });
            } else {
              createM.mutate(payload);
            }
          }}
        />
      )}

      {/* Preview */}
      <CMSPreviewModal
        open={!!preview}
        content={preview}
        onClose={() => setPreview(null)}
      />

      {/* Delete confirm */}
      <AlertDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
      >
        <AlertDialogContent className="border-[#E2E8F0]" data-testid="cms-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0F172A]">
              Delete this content?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#64748B]">
              <span className="font-medium text-[#0F172A]">
                {confirmDel?.title || "This item"}
              </span>{" "}
              will be permanently removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleteM.isPending}
              className="border-[#E2E8F0]"
              data-testid="cms-delete-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (confirmDel) deleteM.mutate(confirmDel.id);
              }}
              disabled={deleteM.isPending}
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
              data-testid="cms-delete-confirm"
            >
              {deleteM.isPending && (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------- Sub-components ----------
function SummaryStats({ counts, activeType, onPick }) {
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4"
      data-testid="cms-summary-cards"
    >
      {CONTENT_TYPES.map((t) => {
        const c = counts?.[t.key] || { published: 0, draft: 0 };
        const on = activeType === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onPick(t.key)}
            className={`text-left p-3 rounded-lg border transition-all ${
              on
                ? "border-[#0F172A] bg-white shadow-sm"
                : "border-[#E2E8F0] bg-white hover:border-[#94A3B8]"
            }`}
          >
            <div className="text-[11px] uppercase tracking-wider text-[#64748B]">
              {t.label}
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-xl font-bold text-[#0F172A]">
                {c.published}
              </span>
              <span className="text-[11px] text-[#16A34A]">Published</span>
            </div>
            <div className="text-[11px] text-[#64748B]">
              {c.draft} draft{c.draft === 1 ? "" : "s"}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function RecentActivity({ items }) {
  if (!items?.length) return null;
  const actionLabel = {
    created: "Created",
    edited: "Edited",
    published: "Published",
    unpublished: "Unpublished",
    deleted: "Deleted",
  };
  const actionColor = {
    published: "#16A34A",
    unpublished: "#B45309",
    deleted: "#DC2626",
    edited: "#0F172A",
    created: "#0F172A",
  };
  return (
    <details
      className="mb-4 rounded-lg border border-[#E2E8F0] bg-white"
      data-testid="cms-activity"
    >
      <summary className="cursor-pointer px-4 py-2.5 text-sm font-medium text-[#0F172A] hover:bg-[#F8FAFC] rounded-t-lg flex items-center gap-2">
        <Filter className="h-4 w-4 text-[#64748B]" />
        Recent Activity ({items.length})
      </summary>
      <ul className="divide-y divide-[#E2E8F0]">
        {items.map((a) => (
          <li
            key={a.id}
            className="px-4 py-2 flex items-center gap-3 text-[12.5px]"
          >
            <span
              className="text-[11px] font-semibold uppercase tracking-wider"
              style={{ color: actionColor[a.action] || "#0F172A" }}
            >
              {actionLabel[a.action] || a.action}
            </span>
            <span className="flex-1 truncate text-[#0F172A]">{a.title}</span>
            <span className="text-[11px] text-[#64748B] shrink-0">
              {formatDateTime(a.at)}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function StatusPill({ status }) {
  const isPub = status === "published";
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
      style={{
        background: isPub ? "#DCFCE7" : "#F1F5F9",
        color: isPub ? "#166534" : "#475569",
      }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: isPub ? "#22C55E" : "#94A3B8" }}
      />
      {isPub ? "Published" : "Draft"}
    </span>
  );
}

function IconBtn({ icon: Icon, onClick, title, danger, testId }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      data-testid={testId}
      className={`h-8 w-8 rounded-md inline-flex items-center justify-center transition-colors ${
        danger
          ? "text-[#DC2626] hover:bg-[#FEE2E2]"
          : "text-[#334155] hover:bg-[#F1F5F9]"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

function Th({ children, className }) {
  return (
    <th
      className={`text-left text-[11px] uppercase tracking-wider font-semibold py-2.5 px-4 ${
        className || ""
      }`}
    >
      {children}
    </th>
  );
}
